import React, { useState } from "react";
import { useData } from "data/data-provider";

interface IconProps {
  fill?: string;
}

const PlusIcon: React.FC<IconProps> = ({ fill }) => (
  <svg
    width="12"
    height="13"
    viewBox="0 0 12 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.16666 5.66675V0.666748H6.83333V5.66675H11.8333V7.33341H6.83333V12.3334H5.16666V7.33341H0.166664V5.66675H5.16666Z"
      fill={fill}
      fillOpacity="0.8"
    />
  </svg>
);
const RemoveIcon: React.FC<IconProps> = ({ fill }) => (
  <svg
    width="12"
    height="3"
    viewBox="0 0 12 3"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.166664 0.666748V2.33341H11.8333V0.666748H0.166664Z"
      fill={fill}
    />
  </svg>
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

const Section: React.FC<SectionProps> = ({
  title,
  children,
  isOpen,
  onToggle,
}) => {
  const { companyDetails } = useData();
  return (
    <div className=" lg:my-4 rounded-md">
      <button
        onClick={onToggle}
        className="flex justify-between w-full space-x-4  py-6 text-left items-center"
      >
        <span className=" font-semibold leading-none text-left text-text-color">
          <span dangerouslySetInnerHTML={{ __html: title }} />
        </span>
        <span>
          {isOpen ? (
            <RemoveIcon fill={companyDetails?.text_color} />
          ) : (
            <PlusIcon fill={companyDetails?.text_color} />
          )}
        </span>
      </button>
      {isOpen && <div className="px-4 py-2">{children}</div>}
    </div>
  );
};

const FAQ: React.FC = () => {
  const { faqs } = useData();
  const [openSectionIndex, setOpenSectionIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setOpenSectionIndex(openSectionIndex === index ? null : index);
  };

  // Calculate midpoint to split FAQs into two columns
  const midpoint = Math.ceil((faqs?.keypoints?.length || 0) / 2);

  // 1NOW layout variant chosen in the FMS (Section.layout_variant):
  //   "accordion"  → click-to-expand rows (default, original render)
  //   "two_column" → static 2-column grid, every Q/A visible at once
  // Default "accordion" preserves the previous render byte-for-byte, which
  // the Design A home and the Design B /rental-policies route both rely on.
  const layoutVariant =
    faqs?.layout_variant === "two_column" ? "two_column" : "accordion";

  // Static two-column variant — reuses RentalPolicies' own container grid,
  // heading/description styling, and the same Q/A classes as the accordion,
  // but shows every pair at once (no click-to-expand, no Section toggle).
  if (layoutVariant === "two_column") {
    return (
      <>
        <div className="container py-12 justify-center mb-8 text-center">
          <h2 className=" font-semibold text-2xl">
            {" "}
            <span className="text-text-color" dangerouslySetInnerHTML={{ __html: faqs?.heading }} />
          </h2>
          <p className="!text-secondary-text-color text-2xl">
            <span className="!text-secondary-text-color " dangerouslySetInnerHTML={{ __html: faqs?.description }} />
          </p>
        </div>
        <div className="container grid grid-cols-1 lg:grid-cols-2 items-start lg:gap-14">
          {faqs?.keypoints?.map((item: any, index: any) => (
            <div key={index} className=" lg:my-4 rounded-md">
              <div className="w-full space-x-4 py-6 text-left">
                <span className=" font-semibold leading-none text-left text-text-color">
                  <span dangerouslySetInnerHTML={{ __html: item?.heading }} />
                </span>
              </div>
              <div className="px-4 py-2">
                <span className="font-normal text-2xl leading-none text-left !text-secondary-text-color">
                  <span className="!text-secondary-text-color" dangerouslySetInnerHTML={{ __html: item?.description }} />
                </span>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="container py-12 justify-center mb-8 text-center">
        <h2 className=" font-semibold text-2xl">
          {" "}
          <span className="text-text-color" dangerouslySetInnerHTML={{ __html: faqs?.heading }} />
        </h2>
        <p className="!text-secondary-text-color text-2xl">
          <span className="!text-secondary-text-color " dangerouslySetInnerHTML={{ __html: faqs?.description }} />
        </p>
      </div>
      <div className="container grid grid-cols-1 lg:grid-cols-2 items-start lg:gap-14">
        {/* First column */}
        <div className="">
          {faqs?.keypoints?.slice(0, midpoint).map((item: any, index: any) => (
            <Section
              key={index}
              title={item?.heading}
              isOpen={openSectionIndex === index}
              onToggle={() => handleToggle(index)}
            >
              <span className="font-normal text-2xl leading-none text-left !text-secondary-text-color">
                <span className="!text-secondary-text-color" dangerouslySetInnerHTML={{ __html: item?.description }} />
              </span>
            </Section>
          ))}
        </div>

        {/* Second column */}
        <div className="">
          {faqs?.keypoints?.slice(midpoint).map((item: any, index: any) => (
            <Section
              key={index + midpoint}
              title={item?.heading}
              isOpen={openSectionIndex === index + midpoint}
              onToggle={() => handleToggle(index + midpoint)}
            >
              <span className="font-normal text-2xl leading-none text-left !text-secondary-text-color">
                <span className="!text-secondary-text-color"  dangerouslySetInnerHTML={{ __html: item?.description }} />
              </span>
            </Section>
          ))}
        </div>
      </div>
    </>
  );
};

export default FAQ;
